// Tiny clipboard helper used across info pages (blog, planner share, contact).
// Uses the modern async Clipboard API and resolves to a boolean so callers can
// decide whether to surface a success or error toast. No DOM side-effects —
// callers are responsible for any UI feedback (matches the pattern in
// `js/pages/blog.ts` and `js/planner-bp/chrome.ts`).
import { showToast } from './utils.ts'
import { t } from './i18n.ts'

/**
 * Copy a string to the OS clipboard. Returns true on success, false otherwise.
 * Quietly no-ops if `navigator.clipboard` is unavailable (insecure contexts /
 * very old browsers).
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!navigator?.clipboard?.writeText) return false
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/**
 * Wires every `<button data-copy-text="...">` inside `root` so that clicking it
 * copies the configured text to the clipboard and shows a toast. Optional
 * `data-copy-success-key` / `data-copy-error-key` attributes override the
 * default i18n keys (`contact.emailCategories.copied` /
 * `contact.emailCategories.copyError`).
 */
export function bindCopyButtons(root: ParentNode = document): void {
  const buttons = root.querySelectorAll<HTMLButtonElement>('button[data-copy-text]')
  buttons.forEach((btn) => {
    if (btn.dataset.copyBound === '1') return
    btn.dataset.copyBound = '1'
    btn.addEventListener('click', async () => {
      const text = btn.dataset.copyText || ''
      if (!text) return
      const successKey = btn.dataset.copySuccessKey || 'contact.emailCategories.copied'
      const errorKey = btn.dataset.copyErrorKey || 'contact.emailCategories.copyError'
      const ok = await copyToClipboard(text)
      if (ok) {
        showToast(t(successKey) || 'Copied', 'success')
      } else {
        showToast(t(errorKey) || 'Could not copy', 'error')
      }
    })
  })
}
